'use client';

import { Anchor } from '@/components/ui/anchor';
import { ContentType } from '@/enums/content-type';
import { useDataAnalytics } from '@/hooks/analytics/analytics';
import { getColor } from '@/utils/class-name-helper';
import { Banner, BannerTile } from '@cinemark/content-api-client';
import clsx from 'clsx';

type BannerTextComponentProps = {
  banner: Banner | BannerTile;
  className?: string;
  dataLinkCategory: string;
  dataLinkName?: string;
  dataLinkNamePrefix?: string;
};

export const BannerTextComponent = ({
  banner,
  className,
  dataLinkCategory,
  dataLinkName,
  dataLinkNamePrefix,
}: BannerTextComponentProps): React.JSX.Element | null => {
  const { trackClick } = useDataAnalytics();

  if (!banner) return null;

  const isTile = banner['@type'] === ContentType.BANNER_TILE;
  const tile = banner as BannerTile;
  const bannerAsBanner = banner as Banner;
  const tileClasses = isTile ? getColor(tile.tileBackgroundColor) : '';
  const heading = isTile ? tile.tileHeading : bannerAsBanner.bannerHeading;
  const headingIconUrl = isTile ? tile.headingIcon?.url : undefined;

  const displayTextButtonLink = (): React.JSX.Element | null => {
    const href = isTile ? tile.tileCTALink : bannerAsBanner.bannerCTALink;
    const linkText = isTile ? tile.tileCTAButton : bannerAsBanner.bannerCTAButton;
    const dataLinkNameValue = [dataLinkNamePrefix, dataLinkName || linkText].filter(Boolean).join(' ');
    const openNewWindow = isTile ? tile.ctaOpenNewWindow : false;

    if (!href || !linkText) return null;

    return (
      <Anchor className="w-full md:w-auto"
        data-test="link-banner-cta"
        href={href}
        rel={openNewWindow ? 'noopener noreferrer' : undefined}
        target={openNewWindow ? '_blank' : '_self'}
        variant="secondary"
        onClick={() => trackClick({
          linkCategory: dataLinkCategory,
          linkName: dataLinkNameValue,
        })}
      >
        {linkText}
      </Anchor>
    );
  };

  return (
    <div className={clsx('flex flex-col items-center justify-center gap-md p-5 text-center', tileClasses, className)}>
      {heading &&
        <h1 data-test="banner-header">
          {headingIconUrl &&
            <img alt=""
              aria-hidden="true"
              className="mr-sm inline-block align-middle"
              data-test="banner-heading-icon"
              src={headingIconUrl}
            />}
          {heading}
        </h1>}
      {isTile ?
        tile.tileParagraph && <p data-test="banner-subheader">{tile.tileParagraph}</p>
        : bannerAsBanner.bannerParagraph && <p data-test="banner-subheader">{bannerAsBanner.bannerParagraph}</p>}
      {displayTextButtonLink()}
    </div>
  );
};
